// Hyperlanes.tsx — The galaxy-map hyperlane network: every link drawn once as a
// single dim LineSegments (one draw call for the whole graph), with the lanes
// touching the hovered system lit up in amber via drei <Line> so you can read a
// system's connections before entering it. Galaxy view only — in system view the
// local gates (see Singularity) stand in for these.
import { useMemo } from 'react';
import { Line } from '@react-three/drei';
import { BufferGeometry, Float32BufferAttribute, Vector3 } from 'three';
import { useGame } from '../state/context';
import type { Galaxy } from '../types';

interface Props {
  galaxy: Galaxy;
}

export function Hyperlanes({ galaxy }: Props) {
  const { hoveredId } = useGame();
  const byId = useMemo(
    () => new Map(galaxy.systems.map((s) => [s.id, s.position])),
    [galaxy.systems],
  );

  // The whole graph as one buffer — flat, dim, and cheap (a single draw call).
  const geometry = useMemo(() => {
    const pts: number[] = [];
    for (const h of galaxy.hyperlanes) {
      const a = byId.get(h.a);
      const b = byId.get(h.b);
      if (!a || !b) {
        continue;
      }
      pts.push(a[0], a[1], a[2], b[0], b[1], b[2]);
    }
    const g = new BufferGeometry();
    g.setAttribute('position', new Float32BufferAttribute(pts, 3));
    return g;
  }, [galaxy.hyperlanes, byId]);

  // The hovered system's own lanes, lit — a handful of segments, drawn with drei
  // <Line> (which owns + disposes its geometry as hover changes).
  const lit = useMemo(() => {
    if (!hoveredId || !byId.has(hoveredId)) {
      return [];
    }
    const lines: Array<[Vector3, Vector3]> = [];
    for (const h of galaxy.hyperlanes) {
      if (h.a !== hoveredId && h.b !== hoveredId) {
        continue;
      }
      const a = byId.get(h.a);
      const b = byId.get(h.b);
      if (!a || !b) {
        continue;
      }
      lines.push([new Vector3(...a), new Vector3(...b)]);
    }
    return lines;
  }, [hoveredId, galaxy.hyperlanes, byId]);

  return (
    <group>
      <lineSegments geometry={geometry} renderOrder={-1}>
        <lineBasicMaterial
          color="#4aa6dd"
          transparent
          opacity={0.42}
          depthWrite={false}
          // Always legible on the open map: skip the depth test so the luminous
          // backdrop (core bulge, nebulae, dust) can never occlude the network.
          depthTest={false}
          toneMapped={false}
        />
      </lineSegments>
      {lit.map((pts, i) => (
        <Line
          key={i}
          points={pts}
          color="#ffb15a"
          transparent
          opacity={0.85}
          lineWidth={1.5}
          depthWrite={false}
        />
      ))}
    </group>
  );
}
